/* ==========================================================================
   WashWax header behaviour
   - Live search results under the search box (Shopify predictive search)
   - Department menu: dropdowns and mega menus (hover intent, click, keyboard)
   - Slide-in menu on a native <dialog>, drilling down level by level
   - Hide-on-scroll-down header (comes back after a longer scroll up)
   - Phone announcement bar: one message at a time
   The cart button is handled by the theme's own cart drawer (id="cart-icon").
   ========================================================================== */
(() => {
  'use strict';
  if (window.__wwhdLoaded) return;
  window.__wwhdLoaded = true;

  const esc = (str) => String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /**
   * Formats a price from predictive search ("11.95") with the shop's money format.
   * @param {string|number} value Price in major units.
   * @param {string} format Shopify money format.
   * @returns {string} Formatted price.
   */
  function money(value, format) {
    const n = Number(value);
    if (Number.isNaN(n)) return '';
    const fixed = n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const fmt = format || '${{amount}}';
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, fixed);
  }

  /* ---------------- Live search ---------------- */
  function initSearch(root) {
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';
    const input = root.querySelector('[data-wwhd-input]');
    const panel = root.querySelector('[data-wwhd-results]');
    if (!input || !panel) return;
    const suggestUrl = root.dataset.suggestUrl || '/search/suggest';
    const searchUrl = root.dataset.searchUrl || '/search';
    const format = root.dataset.moneyFormat;
    let timer = null;
    let controller = null;
    let focusIndex = -1;

    const open = () => { panel.hidden = false; input.setAttribute('aria-expanded', 'true'); };
    const close = () => { panel.hidden = true; input.setAttribute('aria-expanded', 'false'); focusIndex = -1; };
    const options = () => Array.from(panel.querySelectorAll('.wwhd-result'));

    const setFocus = (index) => {
      const items = options();
      if (!items.length) return;
      focusIndex = (index + items.length) % items.length;
      items.forEach((el, i) => {
        el.classList.toggle('is-focus', i === focusIndex);
        el.setAttribute('aria-selected', i === focusIndex ? 'true' : 'false');
      });
      items[focusIndex].scrollIntoView({ block: 'nearest' });
    };

    async function search(term) {
      if (controller) controller.abort();
      controller = 'AbortController' in window ? new AbortController() : null;
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const res = await fetch(`${suggestUrl}.json?${params}`, controller ? { signal: controller.signal } : {});
        if (!res.ok) throw new Error(res.status);
        const data = await res.json();
        render(term, (data.resources && data.resources.results) || {});
      } catch (e) {
        if (e.name !== 'AbortError') close();
      }
    }

    function render(term, results) {
      const products = results.products || [];
      const queries = (results.queries || []).filter((q) => q.text.toLowerCase() !== term.toLowerCase()).slice(0, 3);
      const allUrl = `${searchUrl}?q=${encodeURIComponent(term)}&type=product&options%5Bprefix%5D=last`;
      let html = '';
      if (queries.length) {
        html += queries.map((q) => `<a class="wwhd-result wwhd-result--query" role="option" href="${esc(q.url)}"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/></svg><span>${q.styled_text || esc(q.text)}</span></a>`).join('');
      }
      if (products.length) {
        html += '<p class="wwhd-results__label">Products</p>';
        html += products.map((p) => {
          const img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
          const sized = img ? `${img}${img.includes('?') ? '&' : '?'}width=120` : '';
          const was = Number(p.compare_at_price_max) > Number(p.price) ? `<s>${money(p.compare_at_price_max, format)}</s>` : '';
          return `<a class="wwhd-result" role="option" href="${esc(p.url)}">${sized ? `<img class="wwhd-result__img" src="${esc(sized)}" alt="" width="48" height="48" loading="lazy">` : '<span class="wwhd-result__img"></span>'}<span class="wwhd-result__body"><span class="wwhd-result__title">${esc(p.title)}</span><span class="wwhd-result__price">${money(p.price, format)}${was}</span></span></a>`;
        }).join('');
      } else {
        html += `<p class="wwhd-results__empty">No products match "${esc(term)}".</p>`;
      }
      html += `<a class="wwhd-result wwhd-result--all" role="option" href="${esc(allUrl)}">See all results for "${esc(term)}"</a>`;
      panel.innerHTML = html;
      focusIndex = -1;
      open();
    }

    input.addEventListener('input', () => {
      const term = input.value.trim();
      clearTimeout(timer);
      if (term.length < 2) { close(); return; }
      timer = setTimeout(() => search(term), 200);
    });
    input.addEventListener('focus', () => { if (input.value.trim().length >= 2 && panel.innerHTML) open(); });
    input.addEventListener('keydown', (evt) => {
      if (panel.hidden) return;
      if (evt.key === 'ArrowDown') { evt.preventDefault(); setFocus(focusIndex + 1); }
      else if (evt.key === 'ArrowUp') { evt.preventDefault(); setFocus(focusIndex - 1); }
      else if (evt.key === 'Enter' && focusIndex > -1) { evt.preventDefault(); options()[focusIndex].click(); }
      else if (evt.key === 'Escape') { close(); }
    });
    document.addEventListener('click', (evt) => { if (!root.contains(evt.target)) close(); });
  }


  /* ---------------- Department menu (dropdowns + mega menus) ---------------- */
  function initMenu(head) {
    const nav = head.querySelector('[data-wwhd-nav]');
    if (!nav || nav.dataset.ready) return;
    nav.dataset.ready = 'true';
    const items = Array.from(nav.querySelectorAll('.wwhd-nav__item.has-panel'));
    const hoverMode = head.dataset.openOn !== 'click';
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    let openItem = null;
    let openTimer = null;
    let closeTimer = null;

    const setOpen = (item, open) => {
      const toggle = item.querySelector('[data-wwhd-toggle]');
      item.classList.toggle('is-open', open);
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        // Keep dropdown flyouts on screen near the right edge.
        const drop = item.querySelector('.wwhd-drop');
        if (drop) {
          const rect = drop.getBoundingClientRect();
          drop.classList.toggle('is-flip', rect.right + 240 > window.innerWidth);
        }
      }
    };
    const closeAll = () => {
      clearTimeout(openTimer);
      if (openItem) setOpen(openItem, false);
      openItem = null;
      head.classList.remove('is-menu-open');
    };
    const open = (item) => {
      clearTimeout(closeTimer);
      if (openItem === item) return;
      if (openItem) setOpen(openItem, false);
      openItem = item;
      setOpen(item, true);
      head.classList.add('is-menu-open');
    };
    head.wwhdCloseMenu = closeAll;

    items.forEach((item) => {
      const toggle = item.querySelector('[data-wwhd-toggle]');
      const panel = item.querySelector('[data-wwhd-panel]');

      if (toggle) {
        toggle.addEventListener('click', () => {
          if (openItem === item) closeAll();
          else open(item);
        });
      }

      // Hover intent: a short delay so menus don't flash when the mouse passes over.
      item.addEventListener('pointerenter', (evt) => {
        if (!hoverMode || evt.pointerType !== 'mouse' || !canHover.matches) return;
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        openTimer = setTimeout(() => open(item), openItem ? 0 : 90);
      });
      item.addEventListener('pointerleave', (evt) => {
        if (!hoverMode || evt.pointerType !== 'mouse') return;
        clearTimeout(openTimer);
        closeTimer = setTimeout(() => { if (openItem === item) closeAll(); }, 220);
      });

      // Keyboard: arrow down opens and moves into the menu, Escape closes.
      item.addEventListener('keydown', (evt) => {
        if (evt.key === 'Escape' && openItem === item) {
          evt.stopPropagation();
          closeAll();
          if (toggle) toggle.focus();
        } else if (evt.key === 'ArrowDown' && (evt.target === toggle || evt.target.matches('[data-wwhd-top]'))) {
          evt.preventDefault();
          open(item);
          const first = panel && panel.querySelector('a');
          if (first) first.focus();
        }
      });
      // Close when focus leaves the item entirely.
      item.addEventListener('focusout', (evt) => {
        if (openItem === item && !item.contains(evt.relatedTarget)) closeAll();
      });
    });

    // Left/right arrows move between top-level items.
    nav.addEventListener('keydown', (evt) => {
      if (evt.key !== 'ArrowRight' && evt.key !== 'ArrowLeft') return;
      if (!evt.target.matches('.wwhd-nav__link')) return;
      const links = Array.from(nav.querySelectorAll('.wwhd-nav__link'));
      const i = links.indexOf(evt.target);
      const next = links[(i + (evt.key === 'ArrowRight' ? 1 : -1) + links.length) % links.length];
      if (next) { evt.preventDefault(); closeAll(); next.focus(); }
    });

    document.addEventListener('click', (evt) => { if (openItem && !nav.contains(evt.target)) closeAll(); });
    document.addEventListener('keydown', (evt) => { if (evt.key === 'Escape') closeAll(); });
    window.addEventListener('resize', closeAll, { passive: true });
  }

  /* ---------------- Slide-in menu (native dialog, drill-down panels) ---------------- */
  function initDrawer(head) {
    const section = head.closest('.shopify-section') || head.parentElement;
    const dialog = section && section.querySelector('[data-wwhd-drawer]');
    const openers = section ? Array.from(section.querySelectorAll('[data-wwhd-drawer-open]')) : [];
    if (!dialog || dialog.dataset.ready || typeof dialog.showModal !== 'function') return;
    dialog.dataset.ready = 'true';
    const panels = Array.from(dialog.querySelectorAll('.wwhd-panel'));
    const byKey = (key) => panels.find((p) => p.dataset.panel === key);
    let stack = ['root'];
    let lastOpener = null;

    const show = (key, direction) => {
      const target = byKey(key);
      if (!target) return;
      panels.forEach((p) => {
        const active = p === target;
        p.classList.toggle('is-active', active);
        p.classList.toggle('is-past', !active && stack.includes(p.dataset.panel));
        p.inert = !active;
      });
      target.scrollTop = 0;
      const focusEl = target.querySelector(direction === 'back' ? `[data-wwhd-go="${stack.lastPopped || ''}"]` : '.wwhd-panel__back, .wwhd-panel__row');
      if (focusEl) focusEl.focus({ preventScroll: true });
    };
    const reset = () => {
      stack = ['root'];
      panels.forEach((p) => {
        const root = p.dataset.panel === 'root';
        p.classList.toggle('is-active', root);
        p.classList.remove('is-past');
        p.inert = !root;
      });
    };

    const openDrawer = (opener) => {
      if (head.wwhdCloseMenu) head.wwhdCloseMenu();
      lastOpener = opener || null;
      reset();
      dialog.showModal();
      openers.forEach((b) => b.setAttribute('aria-expanded', 'true'));
    };
    const closeDrawer = () => { if (dialog.open) dialog.close(); };

    openers.forEach((btn) => btn.addEventListener('click', () => openDrawer(btn)));
    dialog.addEventListener('close', () => {
      openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (lastOpener) lastOpener.focus({ preventScroll: true });
    });
    // Tap on the dimmed area closes it.
    dialog.addEventListener('click', (evt) => { if (evt.target === dialog) closeDrawer(); });
    dialog.addEventListener('click', (evt) => {
      const go = evt.target.closest('[data-wwhd-go]');
      const back = evt.target.closest('[data-wwhd-back]');
      const close = evt.target.closest('[data-wwhd-drawer-close]');
      if (close) { closeDrawer(); return; }
      if (go) { stack.push(go.dataset.wwhdGo); show(go.dataset.wwhdGo, 'forward'); return; }
      if (back && stack.length > 1) { stack.lastPopped = stack.pop(); show(stack[stack.length - 1], 'back'); }
    });
    // Swipe left to close, like native apps.
    let startX = null;
    dialog.addEventListener('touchstart', (evt) => { startX = evt.touches[0].clientX; }, { passive: true });
    dialog.addEventListener('touchend', (evt) => {
      if (startX === null) return;
      const dx = evt.changedTouches[0].clientX - startX;
      startX = null;
      if (dx < -70 && stack.length === 1) closeDrawer();
      else if (dx > 70 && stack.length > 1) { stack.lastPopped = stack.pop(); show(stack[stack.length - 1], 'back'); }
    }, { passive: true });
    // Close after following a link (keeps the back button behaving).
    dialog.addEventListener('click', (evt) => { if (evt.target.closest('a[href]')) setTimeout(closeDrawer, 50); });
    // Grows to desktop with the drawer open and no desktop button: close it.
    window.matchMedia('(min-width: 990px)').addEventListener('change', (mq) => {
      if (mq.matches && openers.every((b) => getComputedStyle(b).display === 'none')) closeDrawer();
    });
  }

  /* ---------------- Hide on scroll down, show on scroll up ---------------- */
  function initSmartSticky(head) {
    if (head.dataset.sticky !== 'smart' || head.dataset.stickyReady) return;
    head.dataset.stickyReady = 'true';
    const section = head.closest('.shopify-section') || head.parentElement;
    // Hides after a short scroll down; comes back only after a longer scroll up
    // (or near the top), so small finger movements don't make it jump.
    const HIDE_AFTER = 12;
    const SHOW_AFTER = 180;
    let lastY = window.scrollY;
    let down = 0;
    let up = 0;
    let ticking = false;
    const update = () => {
      ticking = false;
      const y = window.scrollY;
      const dy = y - lastY;
      lastY = y;
      if (dy > 0) { down += dy; up = 0; } else if (dy < 0) { up -= dy; down = 0; }
      const busy = head.classList.contains('is-menu-open') || head.contains(document.activeElement) && document.activeElement.matches('input');
      const past = y > section.offsetHeight + 120;
      if (busy || !past || up >= SHOW_AFTER) { section.classList.remove('wwhd-is-hidden'); if (up >= SHOW_AFTER) up = 0; }
      else if (down >= HIDE_AFTER) { section.classList.add('wwhd-is-hidden'); down = 0; }
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });
    head.addEventListener('focusin', () => section.classList.remove('wwhd-is-hidden'));
  }

  /* ---------------- Phone announcement rotation ---------------- */
  function initAnnouncement(root) {
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';
    const msgs = Array.from(root.querySelectorAll('.wwhd-ann__msg'));
    if (msgs.length < 2) return;
    const mq = window.matchMedia('(max-width: 749px)');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const speed = (parseInt(root.dataset.speed, 10) || 5) * 1000;
    let index = 0;
    setInterval(() => {
      if (!mq.matches || reduce.matches || root.matches(':hover')) return;
      msgs[index].classList.remove('is-active');
      index = (index + 1) % msgs.length;
      msgs[index].classList.add('is-active');
    }, speed);
  }

  function init(scope) {
    scope.querySelectorAll('[data-wwhd-search]').forEach(initSearch);
    scope.querySelectorAll('[data-wwhd-ann]').forEach(initAnnouncement);
    scope.querySelectorAll('[data-wwhd-head]').forEach((head) => {
      initMenu(head);
      initDrawer(head);
      initSmartSticky(head);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (evt) => init(evt.target));

  // Theme editor: show a menu while its block is selected.
  document.addEventListener('shopify:block:select', (evt) => {
    const item = evt.target.closest && evt.target.closest('.wwhd-nav__item.has-panel');
    if (item) { const t = item.querySelector('[data-wwhd-toggle]'); if (t && !item.classList.contains('is-open')) t.click(); }
  });
  document.addEventListener('shopify:block:deselect', (evt) => {
    const head = evt.target.closest && evt.target.closest('[data-wwhd-head]');
    if (head && head.wwhdCloseMenu) head.wwhdCloseMenu();
  });
})();
